/**
 * CACHE POLICY, BY WHAT THE DATA IS — not by which screen happens to show it.
 *
 * The app felt slow because every navigation was a cold fetch: nothing was
 * loaded until it was clicked, and a 30-second blanket `staleTime` meant a
 * screen revisited 31 seconds later paid the full ~1.2s round trip again. That
 * round trip is Railway's public TCP proxy and is NOT something the frontend
 * can shorten — so the fix is to stop waiting for it, not to speed it up.
 *
 * Two independent levers, and they are frequently confused:
 *
 *   staleTime — how long a cached value is served WITHOUT a background refetch.
 *               This is a CORRECTNESS setting. Too long and the screen lies.
 *   gcTime    — how long an UNUSED cache entry survives before eviction. This is
 *               purely a MEMORY setting. A long gcTime never shows stale data on
 *               its own: an entry past its staleTime still refetches on mount,
 *               it just renders the previous value instantly while it does.
 *
 * That distinction is the whole trick. Money queries below use `staleTime: 0`,
 * so they refetch on every single mount and can never be served stale — but
 * their generous `gcTime` means the previous figures paint immediately while
 * the refetch is in flight. The user sees numbers instantly AND sees correct
 * numbers a moment later. Nothing is traded away.
 */

/** Serve-cached-then-revalidate. Never blank, never stale. */
const MINUTE = 60_000;

export const CACHE = {
  /**
   * REFERENCE DATA — the tour catalogue, the permission matrix, the team list.
   *
   * Edited by an administrator a few times a month, read on nearly every
   * screen. Five minutes of staleness costs nothing: if someone renames a tour
   * you may see the old name until you next land on a screen five minutes
   * later, and no money or decision depends on it. Held for half an hour
   * because re-downloading a 9-row catalogue on every visit is the exact waste
   * this change exists to remove.
   */
  reference: { staleTime: 5 * MINUTE, gcTime: 30 * MINUTE },

  /**
   * WORK LISTS — leads, customers, bookings, invoices, vouchers, credit notes.
   *
   * One minute, which is the brief's own example ("a booking list can be stale
   * for a minute"). These are browse-and-scan screens: you open the list to
   * find a row, then click into the detail, which fetches fresh. A row created
   * by a colleague 40 seconds ago being missing is a genuinely acceptable cost
   * for making back-navigation instant — and every mutation that adds a row
   * invalidates the list explicitly anyway, so YOUR OWN writes are never late.
   */
  workList: { staleTime: 1 * MINUTE, gcTime: 15 * MINUTE },

  /**
   * DETAIL RECORDS — one booking, one lead, one customer, one tour, one invoice.
   *
   * Shorter than the lists at 30s, because a detail screen is where people act:
   * they read the status and then decide. Kept for ten minutes so the
   * list -> detail -> back -> detail loop, which is how these screens are
   * actually used, costs one fetch rather than four.
   */
  record: { staleTime: 30_000, gcTime: 10 * MINUTE },

  /**
   * MONEY — ledgers, receivables, payment and invoice summaries, the dashboard,
   * reports.
   *
   * `staleTime: 0` ON PURPOSE, and it is the most important line in this file.
   * Every mount refetches. A payment ledger, an outstanding balance or a
   * receivable is never served from cache without revalidation — the brief's
   * "a cache that shows a stale receivable after a refund is worse than a slow
   * screen", encoded rather than promised.
   *
   * This costs NOTHING perceptually: `gcTime` keeps the last figures around, so
   * react-query paints them immediately and marks the query `isFetching` while
   * the truth is on its way. The alternative — a longer staleTime — would buy
   * no visible speed at all and would risk showing a settled invoice as
   * outstanding.
   *
   * `refetchOnWindowFocus` is ON for this tier only. Someone who leaves the
   * payments screen open, records a payment on their phone and tabs back must
   * not be looking at a stale receivable; for every other tier a focus refetch
   * would be noise.
   */
  money: { staleTime: 0, gcTime: 5 * MINUTE, refetchOnWindowFocus: true },

  /**
   * IMMUTABLE DOCUMENTS — issued receipts.
   *
   * `staleTime: Infinity` is safe here in the strongest possible sense: the API
   * has no update counterpart for a receipt, and wave 2 proved byte-identical
   * receipts across a refund. A fetched receipt can never be wrong, so it is
   * fetched exactly once per session.
   */
  immutable: { staleTime: Infinity, gcTime: 30 * MINUTE },

  /**
   * SHELL CHROME — the derived notification tray and the sidebar counters.
   *
   * A minute. Notifications are derived from live rows on every read, so they
   * self-correct; and every mutation that could change one invalidates this key
   * explicitly (see the audit in the mutation handlers). Held long enough that
   * the tray does not re-fetch on every route change.
   *
   * POLLED — the bell has no websocket, so a colleague's payment or new lead
   * only appears once this ticks. 60s + a focus refetch (someone tabbing back
   * in) is the owner's own "what happened" bar, not a receivable; staleness
   * for a minute costs nothing the way a stale money figure would.
   */
  chrome: {
    staleTime: 1 * MINUTE,
    gcTime: 10 * MINUTE,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  },

  /**
   * THE ACTIVITY FEED — the Dashboard's "Today's activity" panel and the
   * Notifications page's history. Same polling reasoning as `chrome` above,
   * same no-websocket trade-off.
   */
  activity: {
    staleTime: 1 * MINUTE,
    gcTime: 10 * MINUTE,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  },

  /**
   * TYPEAHEAD RESULTS — the TopBar's global search.
   *
   * The odd one out, because the CACHE KEY IS A PREFIX OF WHAT THE USER TYPED.
   * Every keystroke past the minimum length mints a new entry, so this tier is
   * the only one where `gcTime` is a real memory question rather than a
   * free win: a minute of typing can easily produce twenty keys.
   *
   * 30s stale is what makes backspacing feel instant — "guru" -> "gur" -> "guru"
   * is three keys, two of which are already resolved, and a typeahead that
   * re-fetched a term the user had on screen four seconds ago would be visibly
   * slower for no correctness gain. Two minutes of `gcTime` keeps that window
   * open across one search session and then lets the whole tree go; nothing here
   * is worth holding for fifteen minutes, because a search is finished the
   * moment the user clicks a result.
   *
   * `refetchOnWindowFocus` stays off (the default): re-running a search because
   * the user tabbed away and back is noise, not freshness.
   */
  search: { staleTime: 30_000, gcTime: 2 * MINUTE },
} as const;
